<script lang="ts" setup>
import MediaSelectPopup, {
  MediaSelectPopupProps,
} from "@/components/mobile/media/MediaSelectPopup.vue";
import { computed, ref } from "vue";
import { MediaUploadFileItem } from "@/types/component";
import Konva from "konva";
import { addImageToStage } from "@/components/mobile/generation/util";
import { showLoadingToast } from "vant";
import i18n from "@/locales";
import Stage = Konva.Stage;
import Layer = Konva.Layer;
import Image = Konva.Image;

export interface MediaImageShape extends Konva.Image {
  attrs: {
    mediaId: number;
  };
}

interface AddImageToStageProps {
  stage: Stage;
  layer: Layer;
  imageShapes: Image[];
  draggable?: boolean;
  fullScreen?: boolean;
  mediaSelectOpt?: Partial<MediaSelectPopupProps>;
}

const props = withDefaults(defineProps<AddImageToStageProps>(), {
  draggable: true,
  fullScreen: false,
  mediaSelectOpt: () => ({}),
});

const mediaSelectProps = computed(() => {
  return {
    enableGoImageGen: false,
    maxCount: 1,
    multiple: false,
    uploadType: 7,
    mediaType: "img",
    ...props.mediaSelectOpt,
  };
});

const emit = defineEmits<{
  (e: "update:imageShapes", params: Image[]): void;
}>();

const imgList = ref<MediaUploadFileItem[]>([]);
const showMediaSelection = ref(false);

const onMediaSelected = async (list: MediaUploadFileItem[]) => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.loading"),
  });
  try {
    if (mediaSelectProps.value.replace) {
      props.imageShapes.forEach((shape) => shape.destroy());
    }
    for (const item of list) {
      await addImage(item.url!, { mediaId: item.mediaId });
    }
    imgList.value = [];
    showMediaSelection.value = false;
  } finally {
    loading.close();
  }
};

const addImage = async (
  url: string,
  params: {
    transform?: MediaTransform;
    mediaId?: number;
    id?: string;
  } = {}
) => {
  const { imgNode } = await addImageToStage(props.stage, props.layer, url, {
    ...params,
    draggable: props.draggable,
    fullScreen: props.fullScreen,
  });
  emit("update:imageShapes", [...props.imageShapes, imgNode]);
};

export interface AddImageToStageInstance {
  addImage: typeof addImage;
}

defineExpose({
  addImage,
});
</script>
<template>
  <div @click="showMediaSelection = true">
    <slot>
      <button class="btn btn-neutral btn-sm">
        {{ $t("Buttons.insertPicture") }}
      </button>
    </slot>
  </div>
  <media-select-popup
    v-model:media-list="imgList"
    v-model:show="showMediaSelection"
    is-crop-gif
    v-bind="mediaSelectProps"
    @on-media-selected="onMediaSelected"
  />
</template>
